import { classnames } from '@bem-react/classnames'
import { useCallback, useState } from 'react'

import { typedMemo } from '../../../utils'
import { CardGroup } from '../CardGroup'
import type { ActiveTools, OnChangeToolHandler, ToolVariant } from '../types'
import { getDefaultTool } from './helpers'
import css from './ToolBar.css'

/**
 * @param className дополнительные классы, применяемые к верхнеуровневому контейнеру
 * @param tools инструменты для отображения
 * @param onToolChanged обработчик события выбора инструмента
 */
type ToolBarProps<T extends string> = {
  className?: string
  tools: ToolVariant<T>[][]
  onToolChanged: (tool: ToolVariant<T>, rawSet: OnChangeToolHandler<T>) => void
}

function ToolBarImpl<T extends string>({ onToolChanged, tools, className }: ToolBarProps<T>) {
  const [activeTool, setActiveTool] = useState<ActiveTools<T>>(getDefaultTool(tools))

  const rawSetTool = useCallback<OnChangeToolHandler<T>>(newTool => {
    const { toolId, type } = newTool
    switch (type) {
      case 'select':
        setActiveTool(prev => ({ ...prev, select: toolId }))
        break
      case 'activate':
        setActiveTool(prev => {
          if (prev.activate.has(toolId)) {
            prev.activate.delete(toolId)
            return { ...prev, activate: new Set(prev.activate) }
          }

          return { ...prev, activate: new Set(prev.activate.add(toolId)) }
        })
        break
      case 'act':
      case 'container':
        break
    }
  }, [])

  const handleSelect = useCallback(
    (newTool: ToolVariant<T>) => {
      rawSetTool(newTool)
      onToolChanged(newTool, rawSetTool)
    },
    [onToolChanged, rawSetTool],
  )

  return (
    <div className={classnames(css.container, className)}>
      {tools.map((toolsGroup, index) => (
        <CardGroup
          key={index}
          tools={toolsGroup}
          activeTools={activeTool}
          onToolChanged={handleSelect}
        />
      ))}
    </div>
  )
}

/**
 * Панель инструментов
 *
 * Имеет под собой 4 вида инструментов:
 *  - act:
 *    Не имеет включенного/выключенного состояния, отвечает за действие
 *  - activate:
 *    Имеет включенное/выключенное состояния, включает функцию, может быть множество включенных
 *  - select:
 *    Имеет включенное/выключенное состояния, включает режим, включенный инструмент только 1
 *  - container:
 *    Является контейнером, способным содержать внутри себя остальные типы инструментов
 *
 * @typeParam T - набор уникальных id инструментов. Соответствует полю toolId
 * @param {ToolBarProps} смотри {@link ToolBarProps}
 */
export const ToolBar = typedMemo(ToolBarImpl)
